import type { Timestamp, WbsItemDoc } from '@mct/shared';

const MS_PER_DAY = 86_400_000;

function tsToDate(ts: Timestamp | null | undefined): Date | null {
  if (!ts) return null;
  return ts.toDate();
}

export interface DateConstraintViolation {
  /** Which field triggered the violation. */
  field: 'plannedStart' | 'plannedEnd';
  /** The related task's itemId whose schedule conflicts with the candidate. */
  relatedItemId: string;
  /** Display name of the related task (for messaging). */
  relatedName: string;
  /** The boundary date the candidate must respect. */
  boundary: Date;
  /** Human-readable message for the UI. */
  message: string;
}

function fmtDate(d: Date): string {
  return `${d.getUTCFullYear()}/${String(d.getUTCMonth() + 1).padStart(2, '0')}/${String(d.getUTCDate()).padStart(2, '0')}`;
}

/**
 * Returns the latest plannedEnd across predecessors of `item` (tasks listed in
 * item.dependsOn). `null` when no predecessor has a plannedEnd.
 */
export function maxPredecessorEnd(
  item: Pick<WbsItemDoc, 'dependsOn'>,
  allItems: readonly WbsItemDoc[],
): { date: Date; name: string; itemId: string } | null {
  const byId = new Map(allItems.map((i) => [i.itemId, i]));
  let best: { date: Date; name: string; itemId: string } | null = null;
  for (const depId of item.dependsOn ?? []) {
    const dep = byId.get(depId);
    if (!dep) continue;
    const end = tsToDate(dep.plannedEnd);
    if (!end) continue;
    if (!best || end.getTime() > best.date.getTime()) {
      best = { date: end, name: dep.name || dep.itemId, itemId: dep.itemId };
    }
  }
  return best;
}

/**
 * Returns the earliest plannedStart across successors of `item` (tasks whose
 * dependsOn contains item.itemId). `null` when no successor has a plannedStart.
 */
export function minSuccessorStart(
  item: Pick<WbsItemDoc, 'itemId'>,
  allItems: readonly WbsItemDoc[],
): { date: Date; name: string; itemId: string } | null {
  let best: { date: Date; name: string; itemId: string } | null = null;
  for (const other of allItems) {
    if (other.itemId === item.itemId) continue;
    if (!other.dependsOn?.includes(item.itemId)) continue;
    const start = tsToDate(other.plannedStart);
    if (!start) continue;
    if (!best || start.getTime() < best.date.getTime()) {
      best = {
        date: start,
        name: other.name || other.itemId,
        itemId: other.itemId,
      };
    }
  }
  return best;
}

/**
 * Validate a candidate plannedStart against predecessor plannedEnds.
 * Returns a violation when the candidate is strictly before the latest
 * predecessor end. Null candidate = OK (unscheduled).
 */
export function validatePlannedStart(
  candidate: Date | null,
  item: Pick<WbsItemDoc, 'itemId' | 'dependsOn'>,
  allItems: readonly WbsItemDoc[],
): DateConstraintViolation | null {
  if (!candidate) return null;
  const max = maxPredecessorEnd(item, allItems);
  if (!max) return null;
  if (candidate.getTime() >= max.date.getTime()) return null;
  return {
    field: 'plannedStart',
    relatedItemId: max.itemId,
    relatedName: max.name,
    boundary: max.date,
    message: `前工程「${max.name}」の終了日 ${fmtDate(max.date)} 以降に設定してください`,
  };
}

/**
 * Validate a candidate plannedEnd against successor plannedStarts. If the
 * candidate end falls after any successor's plannedStart, the constraint
 * `successor.plannedStart >= predecessor.plannedEnd` is broken.
 */
export function validatePlannedEnd(
  candidate: Date | null,
  item: Pick<WbsItemDoc, 'itemId'>,
  allItems: readonly WbsItemDoc[],
): DateConstraintViolation | null {
  if (!candidate) return null;
  const min = minSuccessorStart(item, allItems);
  if (!min) return null;
  if (candidate.getTime() <= min.date.getTime()) return null;
  return {
    field: 'plannedEnd',
    relatedItemId: min.itemId,
    relatedName: min.name,
    boundary: min.date,
    message: `後工程「${min.name}」の開始日 ${fmtDate(min.date)} 以前に設定してください`,
  };
}

export interface TopologicalOrderResult {
  order: string[];
  hasCycle: boolean;
}

/**
 * Topological sort of items by dependsOn (predecessor → successor). Cycle-safe:
 * remaining items after cycles are appended in stable order with hasCycle=true.
 */
export function topologicalOrder(
  items: readonly WbsItemDoc[],
): TopologicalOrderResult {
  const idSet = new Set(items.map((i) => i.itemId));
  const indegree = new Map<string, number>();
  const deps = new Map<string, string[]>();
  for (const it of items) {
    deps.set(it.itemId, []);
    indegree.set(it.itemId, 0);
  }
  for (const it of items) {
    for (const pred of it.dependsOn ?? []) {
      if (!idSet.has(pred)) continue;
      deps.get(pred)!.push(it.itemId);
      indegree.set(it.itemId, (indegree.get(it.itemId) ?? 0) + 1);
    }
  }
  const queue: string[] = [];
  for (const it of items) {
    if ((indegree.get(it.itemId) ?? 0) === 0) queue.push(it.itemId);
  }
  const order: string[] = [];
  while (queue.length > 0) {
    const id = queue.shift()!;
    order.push(id);
    for (const child of deps.get(id) ?? []) {
      const next = (indegree.get(child) ?? 0) - 1;
      indegree.set(child, next);
      if (next === 0) queue.push(child);
    }
  }
  if (order.length === items.length) return { order, hasCycle: false };
  // Fall back: append the unvisited items in input order so we still return
  // every id. Callers can check hasCycle and bail if that matters.
  const visited = new Set(order);
  for (const it of items) {
    if (!visited.has(it.itemId)) order.push(it.itemId);
  }
  return { order, hasCycle: true };
}

export interface ProposedShift {
  itemId: string;
  /** New plannedStart after shifting (UTC midnight). */
  plannedStart: Date;
  /** New plannedEnd after shifting (UTC midnight). Duration preserved. */
  plannedEnd: Date;
  /** The predecessor whose end forced the shift (for logs). */
  forcedBy: string;
}

/**
 * Walks `items` in topological order and proposes new (plannedStart, plannedEnd)
 * pairs so every successor starts on-or-after its latest predecessor's end.
 *
 * - Preserves each task's existing duration (plannedEnd - plannedStart).
 * - Uses the *proposed* dates of predecessors (cascading shifts).
 * - Ignores rows with null plannedStart/plannedEnd — the aggregator owns
 *   parent rows and unscheduled tasks cannot violate the constraint.
 */
export function computeDateShifts(
  items: readonly WbsItemDoc[],
): {
  shifts: ProposedShift[];
  hasCycle: boolean;
} {
  const { order, hasCycle } = topologicalOrder(items);
  const byId = new Map(items.map((i) => [i.itemId, i]));
  const effectiveStart = new Map<string, Date>();
  const effectiveEnd = new Map<string, Date>();
  const shifts: ProposedShift[] = [];

  for (const it of items) {
    const s = tsToDate(it.plannedStart);
    const e = tsToDate(it.plannedEnd);
    if (s) effectiveStart.set(it.itemId, s);
    if (e) effectiveEnd.set(it.itemId, e);
  }

  for (const id of order) {
    const item = byId.get(id);
    if (!item) continue;
    const curStart = effectiveStart.get(id);
    const curEnd = effectiveEnd.get(id);
    if (!curStart || !curEnd) continue;
    let requiredStart: Date | null = null;
    let forcedBy = '';
    for (const depId of item.dependsOn ?? []) {
      const depEnd = effectiveEnd.get(depId);
      if (!depEnd) continue;
      if (!requiredStart || depEnd.getTime() > requiredStart.getTime()) {
        requiredStart = depEnd;
        forcedBy = depId;
      }
    }
    if (!requiredStart) continue;
    if (curStart.getTime() >= requiredStart.getTime()) continue;
    const durationMs = curEnd.getTime() - curStart.getTime();
    const newStart = requiredStart;
    const newEnd = new Date(newStart.getTime() + durationMs);
    effectiveStart.set(id, newStart);
    effectiveEnd.set(id, newEnd);
    shifts.push({
      itemId: id,
      plannedStart: newStart,
      plannedEnd: newEnd,
      forcedBy,
    });
  }

  return { shifts, hasCycle };
}

export const DAY_MS = MS_PER_DAY;
